/**
 * Entrepreneurial Synergy Type Definitions
 *
 * These types capture operator-specific advantages that may increase
 * the entrepreneurial value of a gas station property beyond its base valuation.
 */

export interface EntrepreneurialSynergyAnswers {
  // Strategic Synergies
  rebrandToExistingBrand: boolean;          // +3% - Brand synergy
  integrateFuelSupply: boolean;             // +3% - Cost structure optimization
  contributesToTerritory: boolean;          // +2% - Network synergy

  // Operational Efficiencies
  bringExistingMgmtOps: boolean;            // +2% - Management efficiency
  reduceCorporateOverhead: boolean;         // +2% - Economies of scale

  // Value Enhancement
  plannedCapexModernization: boolean;       // +3% - Proactive value growth

  // Experience Factor
  operatesOtherLocations: boolean;          // Baseline - No penalty if true, -3% if false
}

export const defaultSynergyAnswers: EntrepreneurialSynergyAnswers = {
  rebrandToExistingBrand: false,
  bringExistingMgmtOps: false,
  integrateFuelSupply: false,
  reduceCorporateOverhead: false,
  contributesToTerritory: false,
  plannedCapexModernization: false,
  operatesOtherLocations: true, // Default to true to avoid penalty
};

/**
 * Calculates the entrepreneurial value multiplier based on synergy answers
 *
 * @param answers - The synergy questionnaire answers
 * @returns Object containing the multiplier and breakdown of factors
 */
export function calculateEntrepreneurialMultiplier(answers: EntrepreneurialSynergyAnswers): {
  multiplier: number;
  uplift: number;
  factors: Array<{ name: string; impact: number }>;
} {
  // Baseline uplift (10% default)
  let uplift = 0.10;
  const factors: Array<{ name: string; impact: number }> = [];

  // Strategic Synergies
  if (answers.rebrandToExistingBrand) {
    uplift += 0.03;
    factors.push({ name: 'Brand synergy', impact: 0.03 });
  }

  if (answers.integrateFuelSupply) {
    uplift += 0.03;
    factors.push({ name: 'Supply integration', impact: 0.03 });
  }

  if (answers.contributesToTerritory) {
    uplift += 0.02;
    factors.push({ name: 'Territory expansion', impact: 0.02 });
  }

  // Operational Efficiencies
  if (answers.bringExistingMgmtOps) {
    uplift += 0.02;
    factors.push({ name: 'Management systems', impact: 0.02 });
  }

  if (answers.reduceCorporateOverhead) {
    uplift += 0.02;
    factors.push({ name: 'Shared overhead', impact: 0.02 });
  }

  // Value Enhancement
  if (answers.plannedCapexModernization) {
    uplift += 0.03;
    factors.push({ name: 'Planned improvements', impact: 0.03 });
  }

  // Experience penalty (only if they don't operate other locations)
  if (!answers.operatesOtherLocations) {
    uplift -= 0.03;
    factors.push({ name: 'First location (learning curve)', impact: -0.03 });
  }

  // Apply diminishing returns if 5+ synergies checked
  const synergyCount = Object.values(answers).filter(Boolean).length;
  if (synergyCount >= 5) {
    const diminishingFactor = 0.9; // 10% reduction
    const originalUplift = uplift;
    uplift = uplift * diminishingFactor;
    factors.push({
      name: 'Diminishing returns adjustment',
      impact: uplift - originalUplift
    });
  }

  // Cap at 25% maximum uplift
  uplift = Math.min(uplift, 0.25);

  // Floor at 5% minimum (even with penalty)
  uplift = Math.max(uplift, 0.05);

  return {
    multiplier: 1 + uplift,
    uplift,
    factors
  };
}
